import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import {
    Typography,
    Box,
    FormGroup,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    createTheme,
    ThemeProvider,
} from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { SwitchWithCustomText, ISwitchWithCustomText } from '../Switch/SwitchWithCustomText';
import { IUserConfiguration, WebCommunicationPreference } from '../../redux/configuration/types';
import { useDispatch } from 'react-redux';
import { toggleAccountModal } from '../../redux/appConfigurator/action';
import { communication } from '../EsSettings/settings.data';

export interface ISettingsProps extends Omit<ISwitchWithCustomText, 'header' | 'body' | 'theme'> {
    onChange: (payload: any) => void;
    setting: IUserConfiguration;
}

const AccountCommunications: React.FC<ISettingsProps> = props => {
    const { onChange, setting } = props;
    const [settingValue, setSettingsValue] = useState<any>();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(toggleAccountModal({ on: false }));

        if (setting) {
            setSettingsValue({
                web_communication_preference: setting.web_communication_preference,
                web_notifications_settings: setting.web_notifications_settings,
                preferred_verification: setting.preferred_verification || 'whatsapp',
                appConfig: {
                    show_app_guide: setting.show_app_guide,
                    show_introduction: setting.show_introduction,
                    show_project_guide: setting.show_project_guide,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);

    const switchTheme = createTheme({
        components: {
            MuiSwitch: {
                styleOverrides: {
                    switchBase: {
                        // Controls default (unchecked) color for the thumb
                        color: theme[themeStyle].tag_white,
                    },
                    colorPrimary: {
                        '&.Mui-checked': {
                            // Controls checked color for the thumb
                            color: theme[themeStyle].tag_white,
                        },
                    },
                    track: {
                        // Controls default (unchecked) color for the track
                        opacity: 1,
                        backgroundColor: theme[themeStyle].gray_message,
                        '.Mui-checked.Mui-checked + &': {
                            // Controls checked color for the track
                            opacity: 0.9,
                            backgroundColor: theme[themeStyle].primary_orange,
                        },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        color: theme[themeStyle].gray_message,
                        '&.Mui-checked': {
                            color: theme[themeStyle].primary_orange,
                        },
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    label: {
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: 16,
                        fontWeight: '500',
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        color: theme[themeStyle].primary_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 16,
                        fontWeight: '600',
                        marginBottom: '10px',
                    },
                },
            },
            //@ts-ignore
            custom: {},
        },
    });

    const handleOnToggle = (value: boolean, child: string, parent: string) => {
        setSettingsValue({
            ...settingValue,
            [parent]: {
                ...settingValue[parent],
                [child]: value,
            },
        });
        if (parent === 'appConfig') {
            onChange({
                [child]: value,
            });
        } else {
            onChange({
                ...settingValue,
                [parent]: {
                    ...settingValue[parent],
                    [child]: value,
                },
            });
        }
    };

    const handlePreferredVerificationChange = (value: string) => {
        setSettingsValue({
            ...settingValue,
            preferred_verification: value,
        });
        onChange({
            preferred_verification: value,
        });
    };

    return (
        <ThemeProvider theme={switchTheme}>
            <Box
                style={styles.flexColumn}
                sx={{
                    gap: '30px',
                    width: '100%',
                    height: '100%',
                    p: {
                        xs: 2,
                        sm: 0,
                    },
                }}>
                <Typography style={styles.title}>Communications</Typography>

                {/* Communication Preferences Section */}
                <Box
                    style={styles.flexColumn}
                    sx={{
                        gap: '40px',
                        width: '100%',
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        borderRadius: 3,
                        p: 3,
                    }}>
                    <Box style={styles.flexColumn} sx={{ gap: '10px', maxWidth: '454px' }}>
                        <Typography style={styles.subtitle}>Communication</Typography>
                        <Typography style={styles.text}>
                            Choose how we can contact you. You will only be contacted through your chosen methods.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'flex-start' },
                            justifyContent: { xs: 'center', sm: 'flex-start' },
                            gap: { xs: '20px', sm: '40px' },
                            width: '100%',
                        }}>
                        <FormGroup style={styles.flexColumn} sx={{ gap: '26px' }}>
                            {settingValue &&
                                settingValue.web_communication_preference &&
                                communication.map((set, index) => (
                                    <SwitchWithCustomText
                                        body={set.body}
                                        header={set.header}
                                        theme={themeStyle}
                                        switchProps={{
                                            onChange: e =>
                                                handleOnToggle(e.target.checked, set.service_name, set.parent_name),
                                            checked:
                                                settingValue.web_communication_preference[
                                                    set.service_name as keyof WebCommunicationPreference
                                                ],
                                        }}
                                        key={index}
                                        {...props}
                                    />
                                ))}
                        </FormGroup>
                    </Box>
                </Box>

                {/* Preferred Verification Section */}
                <Box
                    style={styles.flexColumn}
                    sx={{
                        gap: '40px',
                        width: '100%',
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        borderRadius: 3,
                        p: 3,
                    }}>
                    <Box style={styles.flexColumn} sx={{ gap: '10px', maxWidth: '454px' }}>
                        <Typography style={styles.subtitle}>Preferred Verification Method</Typography>
                        <Typography style={styles.text}>
                            Choose your preferred method for account verification and important communications.
                        </Typography>
                    </Box>

                    <FormControl component="fieldset">
                        <FormLabel component="legend">Verification Method</FormLabel>
                        <RadioGroup
                            value={settingValue?.preferred_verification || 'whatsapp'}
                            onChange={e => handlePreferredVerificationChange(e.target.value)}
                            sx={{ gap: '10px' }}>
                            <FormControlLabel value="whatsapp" control={<Radio />} label="WhatsApp" />
                            <FormControlLabel value="sms" control={<Radio />} label="SMS" />
                            <FormControlLabel
                                disabled
                                sx={{
                                    '&.Mui-disabled': {
                                        color: theme[themeStyle].grey, // changes label color
                                    },
                                }}
                                value="email"
                                control={<Radio />}
                                label="Email"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
        },
        flexRowBetween: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        linebreak: {
            width: '100%',
            border: `1px solid ${theme[colorScheme].gray_text}`,
        },
        title: {
            fontSize: 36,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            textAlign: 'left',
            color: theme[colorScheme].filters_white,
        },
        subtitle: {
            fontSize: 16,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            textAlign: 'left',
            color: theme[colorScheme].primary_white,
        },
        text: {
            fontSize: 16,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            textAlign: 'left',
            color: theme[colorScheme].tag_white,
        },
    });

export { AccountCommunications };
